import { zodResolver } from "@hookform/resolvers/zod";
import { Grid2 } from "@mui/material";
import ButtonComponent from "components/ButtonComponent";
import CheckboxComponent from "components/CheckboxComponent";
import TextFieldComponent from "components/TextFieldComponent";
import { AppContext } from "context/AppContextProvider";
import { t } from "i18next";
import { useContext, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useUpdateSmtpIntegrationAsync, useCreateSmtpIntegrationAsync, useGetSmtpIntegrationByIdAsync, useChangeSmtpPasswordAsync } from "services/IntegrationService/SmtpService/hooks/useMutation";
import { SmtpIntegrationFormSchema } from "services/IntegrationService/SmtpService/schemas";
import zod from "zod";

interface SmtpEditFormProps {
  smtpIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type SmtpEditFormInput = zod.infer<typeof SmtpIntegrationFormSchema>;

const SmtpEditForm = ({
  smtpIdToUpdate,
  handleSaveForm,
}: SmtpEditFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const initialPasswordRef = useRef<string>('');
  const { control, handleSubmit, reset } = useForm<SmtpEditFormInput>({
    resolver: zodResolver(SmtpIntegrationFormSchema),
    defaultValues: {
      host: '',
      port: 587,
      enableSsl: false,
      username: '',
      password: '',
      fromAddress: '',
      isActive: true,
      name: '',
      description: '',
    },
  });

  const {
    mutate: mutateCreateSmtpIntegration,
    isLoading: isLoadingCreateSmtpIntegration,
  } = useCreateSmtpIntegrationAsync();
  const {
    mutate: mutateUpdateSmtpIntegration,
    isLoading: isLoadingUpdateSmtpIntegration,
  } = useUpdateSmtpIntegrationAsync();
  const {
    mutateAsync: mutateGetSmtpIntegrationByIdAsync,
    isLoading: isLoadingGetSmtpIntegrationById,
  } = useGetSmtpIntegrationByIdAsync();
  const {
    mutate: mutateChangeSmtpPassword,
    isLoading: isLoadingChangePassword,
  } = useChangeSmtpPasswordAsync();

  const handleSave = (data: SmtpEditFormInput) => {
    const { password, ...updatePayload } = data as any;

    if (smtpIdToUpdate) {
      const passwordChanged = String(password).trim() !== String(initialPasswordRef.current).trim();

      const doUpdate = () => {
        mutateUpdateSmtpIntegration(
          { id: smtpIdToUpdate, data: updatePayload },
          {
            onSuccess: () => {
              saveIntegration('Integração Smtp atualizada com sucesso.');
            },
          },
        );
      };

      if (passwordChanged) {
        mutateChangeSmtpPassword(
          {
            id: smtpIdToUpdate,
            newPassword: password,
            name: data.name,
            description: data.description,
            isActive: data.isActive,
          },
          {
            onSuccess: () => doUpdate(),
            onError: () => showSnackbar('Erro ao atualizar senha', 'error'),
          },
        );
      } else {
        doUpdate();
      }

      return;
    }
    mutateCreateSmtpIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração Smtp criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (smtpIdToUpdate) {
      mutateGetSmtpIntegrationByIdAsync(smtpIdToUpdate).then((r) => {
        initialPasswordRef.current = r.password || '';
        reset(r, { keepDefaultValues: true });
      });
    }
  }, [smtpIdToUpdate]);

  const sanitizePort = (text: string, fallback = 1): number => {

    const numericString = text.replace(/\D/g, '');
    if (numericString === '') return fallback;

    const parsed = parseInt(numericString, 10);
    if (isNaN(parsed)) return fallback;

    return Math.min(Math.max(parsed, 1), 65535);
  };

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.smtpForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.smtpForms.fromAddress')}
              />
            )}
            name={'fromAddress'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.smtpForms.host')}
              />
            )}
            name={'host'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value?.toString() || ''}
                errorMessage={error?.message}
                onChangeText={(text) => {
                  onChange(sanitizePort(text, 1));
                }}
                type="number"
                label={t('integrationsPage.smtpForms.port')}
              />
            )}
            name={'port'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.smtpForms.username')}
              />
            )}
            name={'username'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.smtpForms.password')}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.smtpForms.description')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.smtpForms.enableSsl')}
              />
            )}
            name={'enableSsl'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.smtpForms.isActive')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateSmtpIntegration ||
              isLoadingGetSmtpIntegrationById ||
              isLoadingUpdateSmtpIntegration ||
              isLoadingChangePassword
            }
          >
            {t('integrationsPage.smtpForms.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SmtpEditForm;